import { useEffect } from 'react';
import { Container, Typography, Grid, Box, Card, CardMedia, CardContent, Chip, Divider } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HotelIcon from '@mui/icons-material/Hotel';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { alpha, styled } from '@mui/material/styles';
import Footer from '../components/Footer';
import SectionHeading from '../components/SectionHeading';
import PortfolioArchive from '../components/projects/PortfolioArchive';
import {
  getArchiveByLocation,
  getFeaturedCompleted,
  getPendingProjects,
  getQozProjects,
  type ProjectRecord,
  type ProjectStatus,
} from '../data/projects';
import { portfolioImgSrc, portfolioSrcSet, stemFromPublicImagePath } from '../lib/responsiveWebp';

const OPPORTUNITY_MEDIA_SIZES = '(max-width: 900px) 100vw, 38vw';

const OpportunityCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'projectKind',
})<{ projectKind?: 'qoz' | 'pending' }>(({ theme, projectKind = 'qoz' }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background:
    projectKind === 'pending'
      ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.14)}, #FFFFFF 42%)`
      : '#FFFFFF',
  border: `1px solid ${projectKind === 'pending' ? theme.palette.secondary.main : theme.palette.primary.main}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 5,
    backgroundColor: projectKind === 'pending' ? theme.palette.secondary.main : theme.palette.primary.main,
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.1)',
    border: `1px solid ${projectKind === 'pending' ? theme.palette.secondary.light : theme.palette.primary.light}`,
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

const statusLabels: Record<ProjectStatus, string> = {
  pending: 'Pending',
  completed: 'Completed Project',
};

function getProjectTitle(project: ProjectRecord): string {
  return project.displayTitle ?? project.title;
}

function OpportunityPlaceholder({ project }: { project: ProjectRecord }) {
  const Icon = project.typeCategory === 'commercial' ? StorefrontIcon : project.typeCategory === 'residential' ? ApartmentIcon : HotelIcon;

  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        minHeight: { xs: 260, md: 320 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)}, ${alpha(theme.palette.secondary.main, 0.18)})`,
      })}
    >
      <Icon sx={{ fontSize: 72, color: 'primary.main', opacity: 0.72 }} />
    </Box>
  );
}

function OpportunityCardContent({
  project,
  imageFetchPriority,
}: {
  project: ProjectRecord;
  imageFetchPriority?: 'high' | 'low' | 'auto';
}) {
  const title = getProjectTitle(project);
  const stem = project.image ? stemFromPublicImagePath(project.image) : null;

  return (
    <OpportunityCard projectKind={project.kind}>
      <CardMedia
        component="div"
        sx={{
          width: { xs: '100%', md: '38%' },
          minHeight: { xs: 260, md: 'auto' },
          maxHeight: { xs: 340, md: 'none' },
          lineHeight: 0,
          flexShrink: 0,
        }}
      >
        {stem ? (
          <Box
            component="img"
            src={portfolioImgSrc(stem, 1280)}
            srcSet={portfolioSrcSet(stem)}
            sizes={OPPORTUNITY_MEDIA_SIZES}
            alt={title}
            loading="lazy"
            decoding="async"
            fetchPriority={imageFetchPriority ?? 'auto'}
            sx={{
              width: '100%',
              height: '100%',
              minHeight: { xs: 260, md: 320 },
              maxHeight: { xs: 340, md: 'none' },
              objectFit: 'cover',
              objectPosition: project.imagePosition ?? 'center center',
              display: 'block',
            }}
          />
        ) : (
          <OpportunityPlaceholder project={project} />
        )}
      </CardMedia>
      <CardContent sx={{ flex: 1, p: { xs: 3, md: 4 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 2, alignItems: { xs: 'flex-start', sm: 'flex-start' }, mb: 1.5 }}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.16em' }}>
              {project.eyebrow}
            </Typography>
            <Typography variant="h3" color="primary" sx={{ mt: 0.5 }}>
              {title}
            </Typography>
          </Box>
          <Chip
            label={statusLabels[project.status]}
            size="small"
            color="secondary"
            sx={{ flexShrink: 0, fontWeight: 600 }}
          />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          {project.location}
        </Typography>
        {project.description && (
          <Typography variant="body1" paragraph sx={{ maxWidth: 840 }}>
            {project.description}
          </Typography>
        )}

        {project.details && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5, my: 3 }}>
            {project.details.map((detail) => (
              <Box key={detail.label}>
                <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: '0.12em' }}>
                  {detail.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {detail.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {project.features && (
          <>
            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
              {project.featureHeading ?? 'Focus Areas'}
            </Typography>
            <Box
              component="ul"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                columnGap: 3,
                rowGap: 1,
                pl: 2.5,
                m: 0,
                listStylePosition: 'outside',
              }}
            >
              {project.features.map((feature) => (
                <Typography
                  component="li"
                  key={feature}
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 0, overflowWrap: 'break-word', pr: { sm: 1 } }}
                >
                  {feature}
                </Typography>
              ))}
            </Box>
          </>
        )}
      </CardContent>
    </OpportunityCard>
  );
}

export default function Projects() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const qozProjects = getQozProjects();
  const pendingProjects = getPendingProjects();
  const completedProjects = getFeaturedCompleted();
  const archiveGroups = getArchiveByLocation();

  return (
    <Box sx={{ width: '100%', height: '100%', minWidth: '100vw', minHeight: '100vh', bgcolor: 'background.default', mt: { xs: 0, md: '3.5rem' } }}>
      <Container sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            Projects
          </Typography>
          <Typography
            variant="h2"
            component="h1"
            color="primary.main"
            sx={{
              mt: 0.5,
              fontSize: {
                xs: '2rem',
                sm: '2.5rem',
                md: '3.15rem',
              },
            }}
          >
            Decades of delivered work across the Tahoe Basin.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5, maxWidth: 760, mx: 'auto' }}>
            Scott Development's portfolio spans residential communities, resort-adjacent hospitality, retail, office, and mixed-use projects across environmentally sensitive Western markets.
          </Typography>
        </Box>

        <SectionHeading
          eyebrow="QOZ Opportunities"
          title="Qualified Opportunity Zone opportunities."
          description="Pending resort and residence club opportunities being evaluated through a disciplined QOZ, entitlement, and sustainable development lens."
        />

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {qozProjects.map((project, index) => (
            <Grid item key={project.id} xs={12}>
              <OpportunityCardContent project={project} imageFetchPriority={index === 0 ? 'high' : 'auto'} />
            </Grid>
          ))}
        </Grid>

        {pendingProjects.length > 0 && (
          <>
            <SectionHeading
              eyebrow="Pending Projects"
              title="Additional current projects."
              description="Current project opportunities outside the QOZ opportunity set, presented with available project scale and role information."
            />

            <Grid container spacing={4} sx={{ mb: 8 }}>
              {pendingProjects.map((project) => (
                <Grid item key={project.id} xs={12}>
                  <OpportunityCardContent project={project} />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        <PortfolioArchive groups={archiveGroups} featuredProjects={completedProjects} />
      </Container>
      <Footer />
    </Box>
  );
}
